import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { useSeo } from "@/lib/seo";

const EFFECTIVE_DATE = "June 1, 2026";

const SECTIONS = [
  { id: "agreement", heading: "Agreement", body: [
    "These Terms of Service ('Terms') govern your access to and use of Sisterhood+ ('we', 'us', 'our'), including any related web and mobile apps (the 'Service').",
    "By creating an account, checking the acknowledgment box, or using the Service, you agree to these Terms and to our Privacy Policy. If you do not agree, do not use the Service.",
  ] },
  { id: "eligibility", heading: "1. Eligibility & account responsibilities", body: [
    "You must be at least 18 years old to use Sisterhood+.",
    "You are responsible for keeping your account credentials confidential and for activity that occurs under your account.",
    "You agree to provide accurate information when creating your account and to keep it current.",
  ] },
  { id: "acceptable-use", heading: "2. Acceptable use", body: [
    "You agree NOT to:",
    { list: [
      "Use the Service for any unlawful purpose or in violation of any regulation.",
      "Impersonate another person or misrepresent your affiliation.",
      "Harass, threaten, dox, or discriminate against any user, coach, or clinician.",
      "Scrape, crawl, or extract data using automated tools without our written consent.",
      "Attempt to bypass, disable, or interfere with security features, rate limits, or authentication.",
      "Upload malware, phishing content, or content that infringes another person's rights.",
      "Resell, rent, or commercialize your access to the Service.",
    ] },
  ] },
  { id: "ai", heading: "3. AI-generated content", body: [
    "The AI Coach, daily affirmations, and AI reflections are generated by large language models. AI responses:",
    { list: [
      "May be inaccurate, incomplete, or out of date.",
      "Do not constitute professional advice of any kind (medical, therapeutic, legal, financial, or otherwise).",
      "Should be verified independently before acting on them, especially for health-related decisions.",
      "Are not confidential in the way a session with a licensed therapist would be. Prompts you send are processed by third-party AI providers under standard commercial data-processing terms.",
    ] },
    "We do our best to add safety layers — crisis-language detection, referrals to professional help, and a persistent reminder that AI is a companion, not a clinician — but you remain responsible for your decisions.",
  ] },
  { id: "medical", heading: "4. Mental-health & medical disclaimer", body: [
    { note: "Sisterhood+ is not a medical device, healthcare provider, or therapy service." },
    "Sisterhood+ provides self-directed wellness content, tools, and community. It is not a substitute for advice, diagnosis, or treatment from a licensed clinician. If you have a medical or mental-health condition, consult a qualified professional.",
    "Content on the Service (including AI-generated content, articles, lessons, assessments, and journal reflections) is provided for general informational and self-reflection purposes only.",
  ] },
  { id: "crisis", heading: "5. Crisis & emergency disclaimer", body: [
    { note: "Sisterhood+ IS NOT AN EMERGENCY SERVICE. Do not use it during a crisis." },
    "If you or someone you know is in immediate danger, in crisis, or considering self-harm, please contact your local emergency services or a crisis line right now:",
    { list: [
      "United States: dial or text 988 (Suicide & Crisis Lifeline)",
      "International: visit findahelpline.com for a directory of local crisis lines",
      "For life-threatening emergencies, call your local emergency number (911 in the U.S., 999 in the U.K., 112 in the EU, 000 in Australia, etc.)",
    ] },
  ] },
  { id: "community", heading: "6. Community conduct", body: [
    "Sisterhood+ community spaces are moderated for safety. We reserve the right to remove content or restrict accounts that:",
    { list: [
      "Threaten, harass, or discriminate against others.",
      "Share personal information about another user without consent.",
      "Promote violence, self-harm, or illegal activity.",
      "Solicit clinical advice from other members inappropriately.",
    ] },
  ] },
  { id: "ugc", heading: "7. User-generated content", body: [
    "You retain ownership of the content you create on the Service — journal entries, check-ins, community posts, assessments. You grant us a limited, non-exclusive, worldwide license to store, display, and process that content solely to operate the Service and provide it back to you.",
    "You represent that you have the rights to any content you submit and that it does not infringe on any third party's rights.",
  ] },
  { id: "ip", heading: "8. Intellectual property", body: [
    "Sisterhood+, the Sisterhood+ name and logo, our lessons, growth plans, and product design are owned by Renewing Minds Counseling and Consulting Services and protected by intellectual property laws. Your account gives you a personal, non-transferable, revocable license to use the Service.",
  ] },
  { id: "subscriptions", heading: "9. Subscriptions, payment, cancellation & refunds", body: [
    { subheading: "Membership pricing", text: "Sisterhood+ membership is offered at $14.99 per month or $99.99 per year. Pricing, billing cadence, included features, cancellation, and refund terms are displayed at checkout." },
    { subheading: "Cancellation", text: "You may cancel your membership at any time; access continues through the end of the current billing period." },
  ] },
  { id: "termination", heading: "10. Account suspension & termination", body: [
    "We may suspend or terminate your account if you violate these Terms, engage in fraudulent activity, or if we're required to by law. Where safe and lawful, we will notify you first.",
    "You may terminate your account at any time from Settings. Your data will be deleted per the retention rules in our Privacy Policy.",
  ] },
  { id: "warranty", heading: "11. Warranties & liability limitations", body: [
    "THE SERVICE IS PROVIDED 'AS IS' AND 'AS AVAILABLE' WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.",
    "TO THE MAXIMUM EXTENT PERMITTED BY LAW, SISTERHOOD+ AND ITS SUPPLIERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES RESULTING FROM YOUR USE OF THE SERVICE.",
  ] },
  { id: "governing-law", heading: "12. Governing law & disputes", body: [
    "These Terms are governed by the laws of the United States and the state in which Renewing Minds Counseling and Consulting Services is established, without regard to conflict-of-laws principles.",
  ] },
  { id: "contact", heading: "13. Contact", body: [
    "Questions about these Terms? Contact Renewing Minds Counseling and Consulting Services at sisterhoodplusapp@gmail.com.",
  ] },
];

export default function Terms() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useSeo({
    title: "Terms of Service | Sisterhood+",
    description: "Read the Sisterhood+ Terms of Service, including our wellness, AI, and crisis disclaimers.",
    path: "/terms",
  });
  return (
    <div className="min-h-screen bg-[#FCFAF6] text-[#291B2D]" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-5 py-10 lg:py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#59535B] hover:text-[#291B2D] transition-smooth">
          <ArrowLeft size={15} /> Back
        </Link>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mt-8">Legal</p>
        <h1 className="font-display text-4xl lg:text-5xl mt-2 leading-tight">Terms of Service</h1>
        <p className="text-[#59535B] mt-3">Effective {EFFECTIVE_DATE}</p>

        <div className="mt-6 rounded-2xl border border-[#E9C0C0] bg-[#FBF1F1] p-5 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-[#9E3A3A] shrink-0 mt-0.5" />
          <p className="text-sm text-[#7A2E2E] leading-relaxed">
            Sisterhood+ is not an emergency or crisis service. If you're in danger or thinking about harming yourself, call or text <a href="tel:988" className="underline font-medium">988</a>, or call <a href="tel:911" className="underline font-medium">911</a>.
          </p>
        </div>

        <div className="mt-10 space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.id} data-testid={`terms-section-${s.id}`}>
              <h2 className="font-display text-2xl mb-3">{s.heading}</h2>
              <div className="space-y-3 text-[#59535B] leading-relaxed">
                {s.body.map((b, i) => {
                  if (typeof b === "string") return <p key={i}>{b}</p>;
                  if (b.note) return <p key={i} className="font-semibold text-[#291B2D]">{b.note}</p>;
                  if (b.subheading) return <p key={i}><span className="font-semibold text-[#291B2D]">{b.subheading}: </span>{b.text}</p>;
                  if (b.list) return <ul key={i} className="list-disc pl-5 space-y-1.5">{b.list.map((li, j) => <li key={j}>{li}</li>)}</ul>;
                  return null;
                })}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-14 pt-8 border-t border-[#E5DED4] text-center text-xs text-[#8A838E]">
          © 2026 Renewing Minds Counseling and Consulting Services. All rights reserved.
        </p>
      </div>
    </div>
  );
}
